// ═══════════════════════════════════════════════════════════════════════════════
// PushNotificationPrompt - Popup discret pour demander la permission push
// S'affiche une fois après la première connexion
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Check, Smartphone, Sparkles } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const STORAGE_KEY = 'push_prompt_shown';

export default function PushNotificationPrompt() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const { isSupported, isSubscribed, permission, subscribe, loading } = usePushNotifications();
  
  useEffect(() => {
    // Vérifier si on doit afficher le prompt
    const checkShowPrompt = () => {
      const userId = localStorage.getItem('userId');
      const alreadyShown = localStorage.getItem(STORAGE_KEY);
      
      // Afficher seulement si :
      // - Utilisateur connecté
      // - Push supporté
      // - Pas encore abonné
      // - Permission pas encore demandée (ou accordée mais pas abonné)
      // - Prompt pas encore montré
      if (
        userId &&
        isSupported &&
        !isSubscribed &&
        permission !== 'denied' &&
        !alreadyShown
      ) {
        // Attendre 5 secondes après le chargement pour ne pas être intrusif
        setTimeout(() => {
          setIsVisible(true);
        }, 5000);
      }
    };
    
    checkShowPrompt();
  }, [isSupported, isSubscribed, permission]);
  
  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsVisible(false);
    }
  };
  
  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
  };
  
  const handleMinimize = () => {
    setIsMinimized(true);
  };
  
  if (!isVisible || isSubscribed) return null;
  
  return (
    <AnimatePresence>
      {isMinimized ? (
        // Version minimisée (petit badge cliquable)
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-24 right-4 z-[9998] w-12 h-12 bg-gradient-to-br from-[#F97316] to-[#ea6c0a] rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="push-prompt-minimized"
        >
          <Bell className="w-5 h-5 text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
        </motion.button>
      ) : (
        // Version complète
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-20 right-4 left-4 sm:left-auto sm:w-96 z-[9998] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
          data-testid="push-prompt"
        >
          {/* Header avec gradient */}
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold text-sm">Reste connecté avec ton assistant</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleMinimize}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Minimiser"
              >
                <span className="text-white text-lg leading-none">−</span>
              </button>
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Fermer"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F97316]/20 to-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-[#F97316]" />
              </div>
              <div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Active les notifications pour ne jamais manquer :
                </p>
                <ul className="mt-2 space-y-1">
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    Nouveaux messages
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    Réponses à tes devis
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    Rappels de RDV
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Boutons */}
            <div className="flex gap-2">
              <button
                onClick={handleDismiss}
                className="flex-1 py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Plus tard
              </button>
              <button
                onClick={handleEnable}
                disabled={loading}
                className="flex-1 py-2.5 bg-gradient-to-r from-[#F97316] to-[#ea6c0a] text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="enable-push-btn"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    Activer
                  </>
                )}
              </button>
            </div>
            
            {/* Info appareil */}
            <p className="mt-3 text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
              <Smartphone className="w-3 h-3" />
              Fonctionne sur mobile et desktop
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
